/**
 * @file Typed fetch helpers for the Storyline Help API.
 *
 * Sends `Authorization: Bearer <access_token>` when a token is available. Under SSO,
 * `AuthProvider` registers a getter so each call uses a fresh access token from the SPA SDK.
 */

import { beginApiLoading, endApiLoading } from "./api-loading-bus";

const STORAGE_ACCESS = "sh_accessToken";
const STORAGE_REFRESH = "sh_refreshToken";

export type ApiAccessTokenProvider = () => Promise<string | undefined>;

let accessTokenProvider: ApiAccessTokenProvider | null = null;

/**
 * Called from auth layer when Auth0 session is active. Pass `null` on logout.
 */
export function setApiAccessTokenProvider(provider: ApiAccessTokenProvider | null): void {
  accessTokenProvider = provider;
}

/** Resolves Bearer token for API calls (Auth0 provider or localStorage). */
export async function resolveAccessToken(): Promise<string | null> {
  if (typeof window === "undefined") {
    return null;
  }
  if (accessTokenProvider) {
    try {
      const t = await accessTokenProvider();
      if (t) {
        setAccessTokenOnly(t);
        return t;
      }
    } catch {
      // use storage fallback (e.g. silent renew not ready yet)
    }
  }
  return getAccessToken();
}

/**
 * Base URL for API requests (browser and server).
 */
export function getApiBase(): string {
  return import.meta.env.VITE_API_URL ?? "http://localhost:4000";
}

/**
 * Reads stored access token (client only).
 */
export function getAccessToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem(STORAGE_ACCESS);
}

/**
 * Stores only the Auth0 access token (SSO-only API auth; no refresh token).
 */
export function setAccessTokenOnly(access: string): void {
  localStorage.setItem(STORAGE_ACCESS, access);
  localStorage.removeItem(STORAGE_REFRESH);
}

/**
 * Clears auth tokens.
 */
export function clearTokens(): void {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
}

/**
 * JSON API request with Bearer auth (Auth0 access token).
 * @param path - Path under API base (e.g. `/api/v1/pages`).
 * @param options - Fetch options; body is JSON-serialized when plain object.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit & { json?: unknown } = {}
): Promise<T> {
  beginApiLoading();
  try {
    const { json, ...init } = options;
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    const token = await resolveAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const body =
      json !== undefined ? JSON.stringify(json) : (init.body as BodyInit | undefined);

    const url = `${getApiBase()}${path}`;
    const res = await fetch(url, { ...init, headers, body });

    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(errBody.error ?? res.statusText);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return res.json() as Promise<T>;
  } finally {
    endApiLoading();
  }
}

/**
 * Payload from `POST /api/v1/auth/session` (derived from the **access token**).
 * The SPA merges `roles` (and access-token `sub` fallback) with the Auth0 **ID token** for display name, email, and picture.
 */
export interface SessionUserResponse {
  sub: string;
  /** Null when the access token has no email claim (use ID token on the SPA for display). */
  email: string | null;
  name: string | null;
  roles: string[];
  isSsoUser?: boolean;
}
